import MirrorBackButton from "@/components/MirrorBackButton";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, Sparkles, Trash2, X } from "lucide-react";
import MobileLayout from "@/components/MobileLayout";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import { toast } from "sonner";

interface Snapshot {
  id: string;
  week_start: string;
  week_number: number;
  year: number;
  score: number | null;
  mood_avg: number | null;
  energy_avg: number | null;
  habits_completed: number;
  checkins_count: number;
  has_enough_data: boolean;
}

const MiniGauge = ({ score, size = 40 }: { score: number; size?: number }) => {
  var r = (size - 6) / 2;
  var c = 2 * Math.PI * r;
  var color =
    score >= 85 ? "hsl(187 72% 50%)" :
    score >= 70 ? "hsl(142 71% 45%)" :
    score >= 40 ? "hsl(40 90% 60%)" : "hsl(0 72% 51%)";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(222 18% 14%)" strokeWidth="4" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="4"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ filter: `drop-shadow(0 0 4px ${color})` }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-display text-[11px] font-bold text-foreground">
        {score}
      </span>
    </div>
  );
};

const ScoreHistory = () => {
  const navigate = useNavigate();
  const { t, tf } = useLanguage();
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }
      const { data } = await (supabase as any)
        .from("weekly_snapshots")
        .select("id, week_start, week_number, year, score, mood_avg, energy_avg, habits_completed, checkins_count, has_enough_data")
        .eq("user_id", user.id)
        .eq("deleted_by_user", false)
        .order("week_start", { ascending: false })
        .limit(12);
      setSnapshots(data || []);
    } catch (e) {
      console.error("[ScoreHistory]", e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { error } = await (supabase as any)
        .from("weekly_snapshots")
        .update({ deleted_by_user: true, deleted_at: new Date().toISOString() })
        .eq("id", deleteTarget);
      if (error) throw error;
      toast.success(t("historyScore.deleteSuccess"));
      setDeleteTarget(null);
      await load();
    } catch {
      toast.error(t("historyScore.deleteError"));
    }
    setDeleting(false);
  };

  const stagger = (i: number) => ({ delay: 0.06 + i * 0.07, duration: 0.5 });

  return (
    <MobileLayout className="pb-28 pt-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="flex items-center gap-3 mb-1">
        <MirrorBackButton />
        <div className="flex items-center gap-2">
          <Sparkles size={15} className="text-primary" />
          <p className="text-[9px] font-display tracking-[0.22em] uppercase text-primary/60">
            {t("historyScore.subtitle")}
          </p>
        </div>
      </motion.div>
      <motion.h1 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
        className="font-display text-[26px] font-semibold text-foreground leading-none mb-6">
        {t("historyScore.title")}
      </motion.h1>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {!loading && snapshots.length === 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(1)}
          className="vyv-card p-6 text-center">
          <p className="text-foreground/50 text-[13px] font-body leading-relaxed">
            {t("historyScore.empty")}
          </p>
        </motion.div>
      )}

      {!loading && snapshots.length > 0 && (
        <>
          <div className="space-y-2.5">
            {snapshots.map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(i)}
                className="vyv-card p-4">
                <div className="flex items-center gap-3">
                  {s.score !== null && s.has_enough_data ? (
                    <MiniGauge score={s.score} />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-foreground/[0.04] border border-border/20 flex items-center justify-center shrink-0">
                      <span className="text-[9px] text-muted-foreground font-display">{"\u2014"}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-[13px] font-semibold text-foreground">{"W" + s.week_number + " \u00b7 " + s.year}</span>
                      <span className="text-[10px] text-muted-foreground font-display">
                        {new Date(s.week_start).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </span>
                    </div>
                    {s.has_enough_data && (
                      <p className="text-foreground/40 text-[10px] font-display mt-0.5">
                        {tf("historyScore.dataStats", { mood: s.mood_avg !== null ? String(s.mood_avg) : "-", energy: s.energy_avg !== null ? String(s.energy_avg) : "-", habits: String(s.habits_completed) })}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setDeleteTarget(s.id)}
                    className="p-1.5 text-muted-foreground/30 hover:text-destructive/60 transition-colors shrink-0"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-muted-foreground/30 text-[9px] font-display text-center mt-4 tracking-wide">
            {t("historyScore.footer")}
          </p>
        </>
      )}

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6"
            onClick={() => setDeleteTarget(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="vyv-card p-6 w-full max-w-sm"
            >
              <div className="flex justify-between items-start mb-4">
                <p className="text-foreground text-[14px] font-display font-semibold">{t("historyScore.deleteConfirm")}</p>
                <button onClick={() => setDeleteTarget(null)} className="text-muted-foreground hover:text-foreground">
                  <X size={16} />
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="flex-1 py-2.5 rounded-xl border border-border/30 text-foreground/60 text-[12px] font-display font-medium hover:bg-foreground/5 transition-colors"
                >
                  {t("historyScore.deleteCancel")}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 py-2.5 rounded-xl bg-destructive/15 border border-destructive/25 text-destructive text-[12px] font-display font-medium hover:bg-destructive/25 transition-colors disabled:opacity-40"
                >
                  {deleting ? "..." : t("historyScore.deleteYes")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </MobileLayout>
  );
};

export default ScoreHistory;
